import { IResponse } from '@/types/common';
import {
  IListAgentMessagesRequest,
  IRunAgentRequest,
} from '@/types/request';
import {
  IAgentEngine,
  IHenkaCoverage,
  IHenkaWeeklyRun,
  IAgentPromptActive,
  IAgentPromptVersion,
  IAgentFamilyMembersResponse,
  IAgentFamilyOption,
  IAgentGenerationJob,
  IAgentGenerationLogResponse,
  IAgentMessage,
  IAgentMessageListResponse,
  IStartAgentJobResponse,
} from '@/types/response';
import axiosClient from '../axiosClient';
import { ENDPOINT } from '../endpoint';

class Agent {
  /**
   * henka のカバレッジ（いま判定にかけられる対象の数）。
   * **発火実績とは別物**で、ここは候補の母数。実際に鳴るのはこの数分の1になる。
   */
  getHenkaCoverage = (): Promise<IResponse<IHenkaCoverage>> =>
    axiosClient.get(ENDPOINT.AGENT.HENKA_COVERAGE);

  /** henka 週次バッチのサマリ（新しい順） */
  listHenkaRuns = (limit = 12): Promise<IResponse<IHenkaWeeklyRun[]>> =>
    axiosClient.get(ENDPOINT.AGENT.HENKA_RUNS, { params: { limit } });

  /** エンジン一覧（セレクト用メタ情報） */
  listEngines = (): Promise<IResponse<IAgentEngine[]>> =>
    axiosClient.get(ENDPOINT.AGENT.LIST_ENGINES);

  /**
   * 実行ジョブを開始する（非同期）。202 で { jobId, status } が返る。
   * 完了は getRunJob でポーリングする。
   */
  startRun = (
    body: IRunAgentRequest,
  ): Promise<IResponse<IStartAgentJobResponse>> =>
    axiosClient.post(ENDPOINT.AGENT.START_RUN, body);

  /** 実行ジョブの状態取得（ポーリング用） */
  getRunJob = (jobId: number): Promise<IResponse<IAgentGenerationJob>> =>
    axiosClient.get(ENDPOINT.AGENT.RUN_JOB.replace(':id', String(jobId)));

  /**
   * フクマルからの nudge を指定家族に即時送信する（テスト用・同期）。
   * 種別は家族のメンバー数で自動判定される（kind: 1人=familyInviteNudge /
   * 2名以上=profileNudge）。週次バッチと同じ判定を通る:
   * 未登録メンバーなし=skipped_complete、直近6日以内に同種別を送信済み=
   * skipped_recent、メンバーが取得できない=skipped_no_member、
   * 送信成功=sent（実投稿+push）。
   */
  runProfileNudge = (
    familyId: number,
  ): Promise<
    IResponse<{
      familyId: number;
      result:
        | 'sent'
        | 'skipped_complete'
        | 'skipped_recent'
        | 'skipped_no_member';
      kind: 'profileNudge' | 'familyInviteNudge' | null;
    }>
  > => axiosClient.post(ENDPOINT.AGENT.RUN_PROFILE_NUDGE, { familyId });

  /** 家族セレクタ用の一覧 */
  listFamilies = (
    search?: string,
  ): Promise<IResponse<IAgentFamilyOption[]>> =>
    axiosClient.get(ENDPOINT.AGENT.LIST_FAMILIES, {
      params: search ? { search } : undefined,
    });

  /** 家族確認 + 対象メンバーセレクタ用の家族メンバー一覧 */
  listFamilyMembers = (
    familyId: number,
  ): Promise<IResponse<IAgentFamilyMembersResponse>> =>
    axiosClient.get(
      ENDPOINT.AGENT.LIST_FAMILY_MEMBERS.replace(':id', String(familyId)),
    );

  /** 生成済みメッセージ一覧 */
  listMessages = (
    params: IListAgentMessagesRequest,
  ): Promise<IResponse<IAgentMessageListResponse>> =>
    axiosClient.get(ENDPOINT.AGENT.LIST_MESSAGES, { params });

  /** メッセージ単体取得（ジョブ完了後の本文表示用） */
  getMessage = (id: number): Promise<IResponse<IAgentMessage>> =>
    axiosClient.get(ENDPOINT.AGENT.GET_MESSAGE.replace(':id', String(id)));

  /** メッセージの生成時ログ（検索クエリ・参照 URL・選定ログ） */
  getMessageGenerationLog = (
    id: number,
  ): Promise<IResponse<IAgentGenerationLogResponse>> =>
    axiosClient.get(
      ENDPOINT.AGENT.GET_MESSAGE_GENERATION_LOG.replace(':id', String(id)),
    );

  /** プロンプト: 全キーの ACTIVE 一覧（未seedキーは source='code'） */
  listActivePrompts = (): Promise<IResponse<IAgentPromptActive[]>> =>
    axiosClient.get(ENDPOINT.AGENT.LIST_PROMPTS);

  /** プロンプト: key の全 version 履歴 */
  getPromptVersions = (
    key: string,
  ): Promise<IResponse<IAgentPromptVersion[]>> =>
    axiosClient.get(
      ENDPOINT.AGENT.GET_PROMPT_BY_KEY.replace(':key', encodeURIComponent(key)),
    );

  /** プロンプト: 新 version 作成（activate=true で ACTIVE 切替） */
  createPromptVersion = (body: {
    key: string;
    template: string;
    description?: string;
    activate?: boolean;
  }): Promise<IResponse<IAgentPromptVersion>> =>
    axiosClient.post(ENDPOINT.AGENT.CREATE_PROMPT, body);

  /** プロンプト: 既存 version を ACTIVE に切替 */
  activatePromptVersion = (
    key: string,
    body: { version: number },
  ): Promise<IResponse<IAgentPromptVersion>> =>
    axiosClient.put(
      ENDPOINT.AGENT.ACTIVATE_PROMPT.replace(':key', encodeURIComponent(key)),
      body,
    );
}

export const AgentService = new Agent();
